/**
 * useLocale — the translation hook. Returns the active locale, a setter, and
 * `t(key, vars?)` bound to that locale. Locale lives in the prefs slice so
 * every consumer re-renders on a switch.
 */
import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { setLocale } from "@app/slices/prefsSlice";
import type { Locale } from "@i18n/locales";
import { translate, type MessageKey } from "@i18n/messages";

export function useLocale() {
  const dispatch = useAppDispatch();
  const locale = useAppSelector((s) => s.prefs.locale);

  const t = useCallback(
    (key: MessageKey, vars?: Record<string, string | number>) => translate(locale, key, vars),
    [locale],
  );

  const changeLocale = useCallback(
    (next: Locale) => {
      dispatch(setLocale(next));
    },
    [dispatch],
  );

  return { locale, t, changeLocale };
}
